import API from "../../helpers/network/http";
import {
    Project,
    ProjectAgreement,
    ProjectApplication,
    ProjectContributionPayoutSummary,
    ProjectMember,
    ProjectRole,
    ProjectRoleInvite,
    ProjectWorkspace,
    Reel
} from "../../utils/types";
import {CreateProjectPayload, ProjectFilters, UpdateProjectPayload} from "./types";

export default class ProjectService {
    static async getProjects(query: ProjectFilters): Promise<Project[]> {
        const {data} = await API.get("/projects", {params: query});
        return data.data;
    }

    static async getOwnerProjects(): Promise<Project[]> {
        const {data} = await API.get("/projects/me");
        return data.data;
    }

    static async getProject(id: string): Promise<Project> {
        const {data} = await API.get(`/projects/${id}`);
        return data.data;
    }

    static async createProject(payload: CreateProjectPayload): Promise<Project> {
        const {data} = await API.post("/projects", payload);
        return data.data;
    }

    static async updateProject(projectId: string, payload: UpdateProjectPayload): Promise<Project> {
        const {data} = await API.put(`/projects/${projectId}`, payload);
        return data.data;
    }

    static async getProjectRoles(projectId: string): Promise<ProjectRole[]> {
        const {data} = await API.get(`/projects/${projectId}/roles`);
        return data.data;
    }

    static async createProjectRole(projectId: string, payload: any): Promise<{data: ProjectRole[]; message?: string; warning?: string | null}> {
        const {data} = await API.post(`/projects/${projectId}/roles`, payload);
        return {
            data: data.data,
            message: data.message,
            warning: data.warning ?? null,
        };
    }

    static async updateProjectRole(projectId: string, projectRoleId: string | number, payload: any): Promise<{data: ProjectRole[]; message?: string; warning?: string | null}> {
        const {data} = await API.put(`/projects/${projectId}/roles/${projectRoleId}`, payload);
        return {
            data: data.data,
            message: data.message,
            warning: data.warning ?? null,
        };
    }

    static async deleteProjectRole(projectId: string, projectRoleId: string | number): Promise<ProjectRole[]> {
        const {data} = await API.delete(`/projects/${projectId}/roles/${projectRoleId}`);
        return data.data;
    }

    static async createProjectApplication(payload: any): Promise<ProjectApplication> {
        const {data} = await API.post("/project-applications", payload);
        return data.data;
    }

    static async getReelById(reelId: string): Promise<Reel> {
        const {data} = await API.get(`/reels/${reelId}`);
        return data.data;
    }

    static async getProjectApplications(projectId: string, query?: {status?: string}): Promise<ProjectApplication[]> {
        const {data} = await API.get(`/projects/${projectId}/applications`, {params: query});
        return data.data;
    }

    static async getProjectApplication(projectId: string, applicationId: string): Promise<ProjectApplication> {
        const {data} = await API.get(`/projects/${projectId}/applications/${applicationId}`);
        return data.data;
    }

    static async getMyProjectApplications(): Promise<ProjectApplication[]> {
        const {data} = await API.get("/project-applications/me");
        return data.data;
    }

    static async reviewProjectApplication(projectId: string, applicationId: string | number, payload: any): Promise<ProjectApplication> {
        const {data} = await API.put(`/projects/${projectId}/applications/${applicationId}/review`, payload);
        return data.data;
    }

    static async getProjectMembers(projectId: string): Promise<ProjectMember[]> {
        const {data} = await API.get(`/projects/${projectId}/members`);
        return data.data;
    }

    static async getProjectAgreements(projectId: string): Promise<ProjectAgreement[]> {
        const {data} = await API.get(`/projects/${projectId}/agreements`);
        return data.data;
    }

    static async createProjectAgreement(projectId: string, payload: {project_role_id: string | number; project_member_id: string | number}): Promise<ProjectAgreement> {
        const {data} = await API.post(`/projects/${projectId}/agreements`, payload);
        return data.data;
    }

    static async getAgreement(agreementId: string | number): Promise<ProjectAgreement> {
        const {data} = await API.get(`/agreements/${agreementId}`);
        return data.data;
    }

    static async fundAgreement(agreementId: string | number, payload?: {provider?: 'paystack' | 'stripe'}): Promise<any> {
        const {data} = await API.post(`/agreements/${agreementId}/fund`, payload || {});
        return data.data;
    }

    static async fundProject(projectId: string, payload?: {
        provider?: 'paystack' | 'stripe';
        agreement_ids?: Array<string | number>;
    }): Promise<any> {
        const {data} = await API.post(`/projects/${projectId}/fund`, payload || {});
        return data.data;
    }

    static async releaseAgreement(agreementId: string | number, payload?: {provider_reference?: string}): Promise<ProjectAgreement> {
        const {data} = await API.post(`/agreements/${agreementId}/release`, payload || {});
        return data.data;
    }

    static async refundAgreement(agreementId: string | number): Promise<ProjectAgreement> {
        const {data} = await API.post(`/agreements/${agreementId}/refund`);
        return data.data;
    }

    static async requestAgreementWithdrawal(agreementId: string | number): Promise<any> {
        const {data} = await API.post(`/agreements/${agreementId}/request-withdrawal`);
        return data.data;
    }

    static async getMyContributionPayoutSummary(): Promise<ProjectContributionPayoutSummary> {
        const {data} = await API.get('/project-agreements/me/payout-summary');
        return data.data;
    }

    static async createRoleInvite(projectId: string, projectRoleId: string | number, payload: {invited_email: string}): Promise<ProjectRoleInvite> {
        const {data} = await API.post(`/projects/${projectId}/roles/${projectRoleId}/invites`, payload);
        return data.data;
    }

    static async getProjectRoleInvites(
        projectId: string,
        query?: {status?: string; project_role_id?: string}
    ): Promise<ProjectRoleInvite[]> {
        const {data} = await API.get(`/projects/${projectId}/invites`, {params: query});
        return data.data;
    }

    static async updateProjectRoleInvite(
        projectId: string,
        projectRoleId: string | number,
        inviteId: string | number,
        payload: {action: 'revoke' | 'resend'; note?: string; expires_at?: string}
    ): Promise<ProjectRoleInvite> {
        const {data} = await API.put(`/projects/${projectId}/roles/${projectRoleId}/invites/${inviteId}`, payload);
        return data.data;
    }

    static async getMyRoleInvites(): Promise<ProjectRoleInvite[]> {
        const {data} = await API.get("/project-role-invites/me");
        return data.data;
    }

    static async linkChoirWorkspace(projectId: string, payload: {choir_id: string | number}): Promise<ProjectWorkspace> {
        const {data} = await API.post(`/projects/${projectId}/workspaces/choir-link`, payload);
        return data.data;
    }

    static async syncChoirWorkspace(projectId: string, payload?: {member_ids?: Array<string | number>}): Promise<ProjectWorkspace> {
        const {data} = await API.post(`/projects/${projectId}/workspaces/choir-sync`, payload || {});
        return data.data;
    }
}
